import { useEffect, useState, type FormEvent } from "react";
import {
  Banner,
  BodyText,
  Button,
  FieldSelect,
  FieldTextarea,
  Modal,
} from "@knowtex-ai/components";
import { MC_CUSTOM_PLAYBOOK, type McRun } from "../../lib/mc";
import {
  useMcTargetPlaybooksQuery,
  useStartMcRun,
} from "../../hooks/useMcRuns";
import NewTabLink from "./NewTabLink";

export const MAX_INSTRUCTIONS_CHARS = 50_000;

interface StartRunModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStarted: (run: McRun) => void;
}

export default function StartRunModal({
  open,
  onOpenChange,
  onStarted,
}: StartRunModalProps) {
  const playbooksQuery = useMcTargetPlaybooksQuery();
  const { refetch } = playbooksQuery;
  const playbooks = playbooksQuery.data ?? [];
  // This dialog stays open while "Browse playbooks" is another tab, and the
  // catalog is cached without refetching on focus. Pull a fresh list when the
  // form opens and when the user comes back to this tab.
  useEffect(() => {
    if (!open) return;
    void refetch();
    const refresh = () => {
      if (document.visibilityState === "visible") void refetch();
    };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, [open, refetch]);
  const [choice, setChoice] = useState<string | null>(null);
  const [instructions, setInstructions] = useState("");
  const startRun = useStartMcRun();
  // Until the catalog arrives, the select would show Custom and a click could start the wrong run.
  const waitingForPlaybooks = playbooksQuery.isLoading;

  const selected =
    choice &&
    (choice === MC_CUSTOM_PLAYBOOK || playbooks.some((p) => p.id === choice))
      ? choice
      : ((playbooks.find((p) => p.default) ?? playbooks[0])?.id ??
        MC_CUSTOM_PLAYBOOK);
  const playbook = playbooks.find((p) => p.id === selected);
  const custom = selected === MC_CUSTOM_PLAYBOOK;
  const canSubmit =
    !waitingForPlaybooks && (!custom || instructions.trim().length > 0);

  const close = () => {
    startRun.reset();
    onOpenChange(false);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || waitingForPlaybooks) return;
    startRun.mutate(
      {
        playbook: selected,
        ...(instructions.trim() && { guidanceText: instructions.trim() }),
      },
      {
        onSuccess: (run) => {
          setChoice(null);
          setInstructions("");
          onStarted(run);
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title="Trigger an MC run"
      subtitle="Runs MC on the webapp against gov-nonprod"
      size="medium"
    >
      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[13px] text-gray-700" aria-hidden="true">
              Playbook
            </span>
            <NewTabLink to="/mc/playbooks" size="small">
              Browse playbooks
            </NewTabLink>
          </div>
          <FieldSelect
            aria-label="Playbook"
            items={[
              ...playbooks.map((p) => ({ value: p.id, label: p.title })),
              { value: MC_CUSTOM_PLAYBOOK, label: "Custom" },
            ]}
            value={selected}
            onChange={(value) => value && setChoice(value)}
            disabled={startRun.isPending || waitingForPlaybooks}
            triggerMinWidth="100%"
          />
          {playbooksQuery.isSuccess && !playbooks.length && (
            <BodyText size="small" muted>
              No playbooks yet, so describe what to test below.
            </BodyText>
          )}
          {playbook?.description && (
            <BodyText size="small" muted>
              {playbook.description}
            </BodyText>
          )}
        </div>
        <FieldTextarea
          label="Instructions"
          placeholder={
            custom
              ? "What MC should test, e.g. Check that a recording survives switching tabs."
              : "e.g. Focus on recording with the tab in the background."
          }
          description={
            custom
              ? "Everything MC should do on this run"
              : "Optional additional guidance for this run only"
          }
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          maxLength={MAX_INSTRUCTIONS_CHARS}
          autoComplete="off"
          rows={custom ? 6 : 4}
          required={custom}
          disabled={startRun.isPending || waitingForPlaybooks}
        />
        {startRun.isError && (
          <Banner variant="error" heading="Couldn't start the run">
            {startRun.error.message}
          </Banner>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={close}
            disabled={startRun.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={startRun.isPending}
            disabled={!canSubmit}
          >
            Trigger run
          </Button>
        </div>
      </form>
    </Modal>
  );
}
